<template>
  <ion-page>
    <ion-content :fullscreen="true">
      <!-- Two bands, not one centred stack. The message takes the room it needs
           and stays optically centred; the action sits on the bottom edge where
           a thumb already is, on every screen height. `min-h-full` makes the
           column fill the scroll box so `flex-1` has something to divide. -->
      <div class="flex min-h-full flex-col bg-brown-50 px-5 pb-6 pt-8">
        <div
          class="mx-auto flex w-full max-w-[360px] flex-1 flex-col items-center justify-center text-center"
        >
          <!-- Working. No course name yet: we do not know which course this is
               until IDR answers, and guessing then correcting reads worse than
               waiting a beat. -->
          <template v-if="state === 'loading'">
            <div class="mb-5 flex h-20 w-20 items-center justify-center">
              <DsSpinner size="xl" label="Checking your voucher" class="text-grey-60" />
            </div>
            <div class="mb-2 text-24-lh-24 font-black tracking-tight text-grey-800">
              Checking your voucher
            </div>
            <p class="text-text-body-lg font-medium text-grey-60">This only takes a moment.</p>
          </template>

          <template v-else-if="state === 'unlocked'">
            <div
              class="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-scanner-success/15 text-scanner-success"
            >
              <DsIcon name="check" size="xl" />
            </div>
            <div class="mb-2 text-24-lh-24 font-black tracking-tight text-grey-800">
              {{ unlockedHeadline }}
            </div>
            <p class="text-text-body-lg font-medium text-grey-60">{{ unlockedSubtitle }}</p>
          </template>

          <template v-else>
            <div
              class="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-error text-white"
            >
              <DsIcon name="close" size="xl" />
            </div>
            <div class="mb-2 text-24-lh-24 font-black tracking-tight text-grey-800">
              We couldn’t unlock this
            </div>
            <p class="text-text-body-lg font-medium text-grey-60">{{ errorMessage }}</p>
          </template>
        </div>

        <!-- The action band. Both routes out carry the same weight: leaving a
             refusal is the only thing left to do on that screen, and a bordered
             button on the cream ground gave the member nothing obvious to press. -->
        <div
          v-if="state !== 'loading'"
          class="mx-auto w-full max-w-[360px] pb-[env(safe-area-inset-bottom)] pt-6"
        >
          <DsButton
            v-if="state === 'unlocked'"
            class="w-full"
            variant="secondary"
            size="56"
            shape="pill"
            @click="goToCourse"
          >
            Let’s go!
          </DsButton>
          <DsButton
            v-else
            class="w-full"
            variant="secondary"
            size="56"
            shape="pill"
            @click="goToCourses"
          >
            Back to Next Steps
          </DsButton>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
// Where a scanned unlock card lands.
//
// Cards are read with the PHONE's camera, so the member arrives from a
// universal link with no app screen behind them. Sending them to /scanner —
// a camera UI — while a redemption they could not see ran underneath was
// disorienting: a flash of viewfinder, then a toast, then a course page.
//
// This page owns that moment instead. Three states, one job each, and the
// member always leaves by a button rather than by being thrown somewhere.
//
// It is dressed as the app's other terminal moments are — the payment result
// step and the voucher detail screen — with design-system type, buttons and
// icons rather than a local palette. This screen is app chrome, not reading
// chrome, so it deliberately does not take the courses reader theme.
//
// NOTE ON THE CODE IN THE URL: it is there because the pending deep-link has to
// survive an Auth0 round trip through localStorage. An unlock card is a bearer
// instrument, so `/courses/redeem` is registered as a sensitive route in
// `shouldBlockTracking` — analytics must never record a live code.

import { IonPage, IonContent } from '@ionic/vue';
import {
  Button as DsButton,
  Icon as DsIcon,
  Spinner as DsSpinner,
} from '@team-hogc/hogc-one-design-system';
import { prepareCode, submitCode, REDEEM_OUTCOME } from '@/utils/redeem-card';
import { REDEEM_MESSAGES } from '@/utils/redeem-messages';
import privateRegion from '@/mixins/PrivateRegion';

const ALL_TAB_PATH = '/tabs/all';

export default {
  name: 'CourseRedeemPage',
  mixins: [privateRegion],
  components: { IonPage, IonContent, DsButton, DsIcon, DsSpinner },
  data() {
    return {
      state: 'loading',
      programSlug: null,
      replay: false,
      errorMessage: REDEEM_MESSAGES.DEFAULT,
    };
  },
  computed: {
    // The name arrives with the access refresh the redemption already triggers.
    // When it has not (a raced refresh, or the program is missing from the
    // payload) the headline falls back to the generic noun rather than naming
    // nothing — both halves end in "is unlocked" so the two shapes read as the
    // same sentence with and without the course's name.
    unlockedHeadline() {
      const program = this.$store.getters['courses/programAccess']?.(this.programSlug);
      return program?.name ? `${program.name} is unlocked` : 'Your course is unlocked';
    },
    unlockedSubtitle() {
      return this.replay
        ? 'You unlocked this one earlier — it is still in your account.'
        : 'It has been added to your account.';
    },
  },
  async mounted() {
    await this.redeem();
  },
  methods: {
    async redeem() {
      const raw = this.$route.params.code || this.$route.query.ref || '';
      const prepared = prepareCode(raw);

      // Refused on shape alone, with no network call — so there is nothing to
      // wait for and the spinner would only be theatre.
      if (prepared.error) {
        this.errorMessage = prepared.error;
        this.state = 'error';
        return;
      }

      const result = await submitCode(this.$store, prepared.code);

      if (result.outcome === REDEEM_OUTCOME.NEEDS_CG) {
        // Hand the card over before navigating, so the chooser raises the CG
        // block and finishes this redemption itself. Without this the member
        // would be told to link a CG and then have to find the card again.
        this.$store.commit('courses/setCgPendingRedeemCode', prepared.code);
        this.$router.replace('/courses');
        return;
      }

      if (result.outcome === REDEEM_OUTCOME.REFUSED) {
        this.errorMessage = result.message;
        this.state = 'error';
        return;
      }

      this.programSlug = result.programSlug;
      this.replay = result.replay;
      this.state = 'unlocked';
    },
    goToCourse() {
      // replace, not push: the member should never land back on a redemption
      // that has already happened by swiping back.
      this.$router.replace(`/courses/${this.programSlug}`);
    },
    goToCourses() {
      this.$router.replace(
        this.$store.getters['courses/programVisible']?.('next-steps-p1')
          ? '/courses'
          : ALL_TAB_PATH,
      );
    },
  },
};
</script>

<style scoped>
ion-content {
  --background: theme('colors.brown.50');
}
</style>
